// ConfigTable — options table for one system_config category.
// Every row's label can be edited; active rows can only be disabled, while inactive rows can
// additionally be deleted (two-step safety gate).
import { MPESA_CONFIG_KEYS } from "@dm-lms/shared/constants";
import type { SystemConfig } from "@dm-lms/shared/types";
import DeleteConfigOption from "./DeleteConfigOption.js";
import EditConfigLabel from "./EditConfigLabel.js";

const SKELETON_ROWS = 3;

// What a valid value looks like, for keys whose label is read as a setting rather than display text
const LABEL_HINTS: Record<string, string> = {
  [MPESA_CONFIG_KEYS.stkPushEnabled]: "Enter true to enable STK push, or false to keep manual instructions.",
};

interface ConfigTableProps {
  rows:         SystemConfig[] | null;   // null while loading
  savingId:     string | null;
  onToggle:     (row: SystemConfig) => void;
  onDeleted:    (id: string) => void;
  onLabelSaved: (id: string, label: string) => void;
  canEditLabel?: boolean;   // default true; false hides label editing (e.g. tutor_email, where the label is the address)
}

function ActiveSwitch({ row, disabled, onToggle }: { row: SystemConfig; disabled: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={row.isActive}
      aria-label={`${row.label} active`}
      disabled={disabled}
      onClick={onToggle}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition disabled:opacity-50 ${row.isActive ? "bg-green-500" : "bg-gray-300"}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition ${row.isActive ? "translate-x-4" : "translate-x-0.5"}`}
      />
    </button>
  );
}

export default function ConfigTable({ rows, savingId, onToggle, onDeleted, onLabelSaved, canEditLabel = true }: ConfigTableProps) {
  if (rows !== null && rows.length === 0) {
    return <p className="text-dm-slate text-sm">No options yet. Add one above.</p>;
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-left text-dm-slate">
          <tr>
            <th className="px-4 py-3 font-medium">Label</th>
            <th className="px-4 py-3 font-medium">Key</th>
            <th className="px-4 py-3 font-medium">Active</th>
            <th className="px-4 py-3 font-medium">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows === null
            ? Array.from({ length: SKELETON_ROWS }, (_, i) => (
                <tr key={i} aria-hidden="true">
                  {[0, 1, 2, 3].map((cell) => (
                    <td key={cell} className="px-4 py-3">
                      <div className="h-4 w-24 animate-pulse rounded bg-gray-200" />
                    </td>
                  ))}
                </tr>
              ))
            : rows.map((row) => (
                <tr key={row.id}>
                  <td className="px-4 py-3">{row.label}</td>
                  <td className="px-4 py-3 font-mono text-xs text-dm-slate">{row.key}</td>
                  <td className="px-4 py-3">
                    <ActiveSwitch row={row} disabled={savingId === row.id} onToggle={() => { onToggle(row); }} />
                  </td>
                  <td className="px-4 py-3 text-dm-slate">
                    <div className="flex flex-wrap items-start gap-2">
                      {canEditLabel && (
                        <EditConfigLabel
                          id={row.id}
                          label={row.label}
                          category={row.category}
                          hint={LABEL_HINTS[row.key]}
                          onSaved={onLabelSaved}
                        />
                      )}
                      {!row.isActive && (
                        <DeleteConfigOption id={row.id} label={row.label} category={row.category} onDeleted={onDeleted} />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
        </tbody>
      </table>
    </div>
  );
}
